const { BaseDAO } = require('./BaseDAO.js');

class BlogDAO extends BaseDAO {
  constructor() {
    super();
    this.blogDbPath = '/blog/blog.db.json';
    this.storyBasePath = '/blog/stories';
    this.cacheTTL = 10 * 60 * 1000; // 10 minutes for blog data
  }

  // Core database access
  async getBlogDatabase() {
    return this.fetchWithCache(this.blogDbPath, 'blog_db', this.cacheTTL);
  }

  // Optimized routing parser for new format
  parseRouting(routingValue) {
    if (typeof routingValue === 'string') {
      return { storyId: routingValue, partFolder: '', type: 'standalone' };
    } else if (Array.isArray(routingValue) && routingValue.length === 2) {
      return { storyId: routingValue[0], partFolder: routingValue[1], type: 'series' };
    } else if (typeof routingValue === 'object' && routingValue?.storyId) {
      return routingValue; // Legacy format
    }
    return null;
  }

  // Get routing entry with parsing
  getRoutingEntry(routing, slug) {
    const routingValue = routing[slug];
    return routingValue ? this.parseRouting(routingValue) : null;
  }

  // Resolve denormalized lightweight card from blog.db.json (no per-story fetch).
  // Returns null when the index has no entry, so callers fall back to fetching.
  async getDenormalizedLight(storyId, partFolder = null) {
    try {
      const blogDb = await this.getBlogDatabase();
      const story = blogDb.stories?.find(s => s.id === storyId);
      if (!story) return null;
      if (partFolder) return story.partsLight?.[partFolder] || null;
      return story.light || null;
    } catch (error) {
      return null;
    }
  }

  // Story metadata access with caching
  async getStoryMetadata(storyId, partFolder = null, lightweight = false) {
    const cacheKey = `story_${storyId}${partFolder ? '_' + partFolder : ''}${lightweight ? '_light' : ''}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    // Lists, related posts, sidebars and series navigation only need lightweight
    // cards — serve them from the denormalized blog.db.json index (single cached
    // request) instead of fetching every individual story.json.
    if (lightweight) {
      const denormalized = await this.getDenormalizedLight(storyId, partFolder);
      if (denormalized) {
        this.setCached(cacheKey, denormalized);
        return denormalized;
      }
    }

    const path = partFolder
      ? `${this.storyBasePath}/${storyId}/${partFolder}/story.json`
      : `${this.storyBasePath}/${storyId}/story.json`;

    try {
      const data = await this.fetchWithCache(path, cacheKey, this.cacheTTL);
      
      // Return lightweight version if requested
      if (lightweight) {
        const lightData = {
          id: data.id,
          title: data.title,
          excerpt: data.excerpt,
          category: data.category,
          publishDate: data.publishDate,
          readTime: data.readTime,
          coverImage: data.coverImage,
          tags: data.tags || [],
          author: data.author
        };
        this.setCached(cacheKey, lightData);
        return lightData;
      }
      
      return data;
    } catch (error) {
      console.error(`Failed to load story metadata: ${storyId}${partFolder ? '/' + partFolder : ''}`, error);
      return null;
    }
  }

  // Series metadata access
  async getSeriesMetadata(seriesId) {
    const cacheKey = `series_${seriesId}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    // Serve from the denormalized blog.db.json index when available (avoids a
    // series.json fetch per series on every list/related/sidebar render).
    try {
      const blogDb = await this.getBlogDatabase();
      const story = blogDb.stories?.find(s => s.id === seriesId);
      if (story?.seriesMeta) {
        this.setCached(cacheKey, story.seriesMeta, this.cacheTTL);
        return story.seriesMeta;
      }
    } catch (error) {
      // fall through to direct fetch
    }

    const path = `${this.storyBasePath}/${seriesId}/series.json`;
    try {
      return await this.fetchWithCache(path, cacheKey, this.cacheTTL);
    } catch (error) {
      console.error(`Failed to load series metadata: ${seriesId}`, error);
      return null;
    }
  }

  // Bulk story loading for performance
  async bulkLoadStories(storyRequests) {
    const requests = storyRequests.map(({ storyId, partFolder, lightweight }) => ({
      url: partFolder 
        ? `${this.storyBasePath}/${storyId}/${partFolder}/story.json`
        : `${this.storyBasePath}/${storyId}/story.json`,
      cacheKey: `story_${storyId}${partFolder ? '_' + partFolder : ''}${lightweight ? '_light' : ''}`,
      ttl: this.cacheTTL,
      storyId,
      partFolder,
      lightweight
    }));

    const results = await this.batchFetch(requests);
    
    return results.map(result => ({
      storyId: result.request.storyId,
      partFolder: result.request.partFolder,
      data: result.success ? (result.request.lightweight ? this.extractLightweight(result.data) : result.data) : null,
      error: result.error
    }));
  }

  // Extract lightweight data
  extractLightweight(fullData) {
    if (!fullData) return null;
    return {
      id: fullData.id,
      title: fullData.title,
      excerpt: fullData.excerpt,
      category: fullData.category,
      publishDate: fullData.publishDate,
      readTime: fullData.readTime,
      coverImage: fullData.coverImage,
      tags: fullData.tags || [],
      author: fullData.author
    };
  }

  // Get story content (markdown)
  async getStoryContent(storyId, partFolder = null) {
    const cacheKey = `content_${storyId}${partFolder ? '_' + partFolder : ''}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    const path = partFolder 
      ? `${this.storyBasePath}/${storyId}/${partFolder}/story.md`
      : `${this.storyBasePath}/${storyId}/story.md`;

    try {
      const response = await fetch(path);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const markdown = await response.text();
      this.setCached(cacheKey, markdown);
      return markdown;
    } catch (error) {
      console.error(`Failed to load story content: ${storyId}${partFolder ? '/' + partFolder : ''}`, error);
      throw error;
    }
  }

  // Find routing key for story/part combination
  findRoutingKey(routing, storyId, partFolder = null, type = null) {
    for (const [slug, routingValue] of Object.entries(routing)) {
      const parsed = this.parseRouting(routingValue);
      if (!parsed) continue;
      
      if (parsed.storyId === storyId && 
          (partFolder === null || parsed.partFolder === partFolder) &&
          (type === null || parsed.type === type)) {
        return slug;
      }
    }
    return null;
  }

  // Get all routing entries for a story (useful for series)
  getStoryRoutingEntries(routing, storyId) {
    const entries = [];
    for (const [slug, routingValue] of Object.entries(routing)) {
      const parsed = this.parseRouting(routingValue);
      if (parsed && parsed.storyId === storyId) {
        entries.push({ slug, ...parsed });
      }
    }
    return entries.sort((a, b) => {
      // Sort by type (standalone first, then series), then by part folder
      if (a.type !== b.type) {
        return a.type === 'standalone' ? -1 : 1;
      }
      return a.partFolder.localeCompare(b.partFolder);
    });
  }
}

module.exports = { BlogDAO };